import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Share2, Download, ArrowLeft, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { CLINIC } from "../lib/data";

const SRC_LABEL = { qr: "QR screen", poster: "Poster", tent: "Table tent", banner: "Share banner", dashboard: "App", link: "Link", other: "Other" };

export default function QR() {
  const navigate = useNavigate();
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  const withSrc = (src) => `${appUrl}${appUrl.includes("?") ? "&" : "?"}src=${src}`;
  const qrSrc = `${API}/qrcode?url=${encodeURIComponent(withSrc("qr"))}`;
  const posterSrc = `${API}/qr-poster?url=${encodeURIComponent(withSrc("poster"))}`;
  const tentSrc = `${API}/qr-poster?url=${encodeURIComponent(withSrc("tent"))}&format=tent`;
  const bannerSrc = `${API}/qr-poster?url=${encodeURIComponent(withSrc("banner"))}&format=banner`;

  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/scan-analytics").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const shareLink = async () => {
    const data = { title: "Muse Dental AI", text: "Free AI dental screening — Scan. Screen. Smile.", url: appUrl };
    try {
      if (navigator.share) await navigator.share(data);
      else { await navigator.clipboard.writeText(appUrl); toast.success("Link copied to clipboard"); }
    } catch (e) { /* cancelled */ }
  };

  const downloadQR = async () => {
    try {
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "muse-dental-qr.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("QR code downloaded");
    } catch (e) {
      window.open(qrSrc, "_blank");
    }
  };

  return (
    <div className="min-h-screen w-full flex justify-center">
      <div className="relative w-full max-w-md min-h-screen brand-gradient flex flex-col overflow-hidden">
        {/* subtle tooth motif */}
        <div className="pointer-events-none absolute -top-16 -right-10 w-72 h-72 rounded-full bg-[#8FE3BE]/10 blur-2xl" />
        <div className="pointer-events-none absolute bottom-0 -left-16 w-72 h-72 rounded-full bg-[#8FE3BE]/10 blur-2xl" />

        <button data-testid="qr-back" onClick={() => navigate("/")} className="tap absolute top-4 left-4 z-10 p-2 rounded-full bg-white/10 text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <h1 className="font-head font-extrabold text-3xl tracking-tight text-[#8FE3BE]">MUSE DENTAL AI</h1>
          <p className="mt-1.5 font-head font-bold text-lg text-white">Scan. Screen. Smile.</p>
          <p className="text-white/70 text-sm mt-2 max-w-xs">Share this code so anyone can start a free screening in their phone browser.</p>

          <div className="mt-8 bg-white rounded-3xl p-5 mint-glow">
            <img data-testid="qr-image" src={qrSrc} alt="Muse Dental AI QR code" className="w-60 h-60" />
            <p className="mt-3 text-[13px] font-semibold text-primary">Scan to start your free screening</p>
          </div>

          <div className="mt-5 text-center" data-testid="qr-clinic">
            <p className="font-head font-extrabold text-white text-2xl leading-tight">{CLINIC.name}</p>
            <p className="text-white/75 text-sm mt-1.5">{CLINIC.area} · {CLINIC.phoneDisplay}</p>
          </div>

          <p className="mt-3 text-[11px] tracking-wide text-white/60">OPENS {appUrl}</p>
        </div>

        <div className="px-6 pb-8 flex flex-col gap-3">
          <button data-testid="qr-share" onClick={shareLink}
            className="tap w-full h-13 py-3.5 rounded-2xl bg-white text-primary font-semibold flex items-center justify-center gap-2">
            <Share2 className="w-4 h-4" /> Share link
          </button>
          <button data-testid="qr-download" onClick={downloadQR}
            className="tap w-full h-13 py-3.5 rounded-2xl bg-[#8FE3BE] text-primary font-semibold flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download QR image
          </button>
          <button data-testid="qr-poster" onClick={() => window.open(posterSrc, "_blank")}
            className="tap w-full h-13 py-3.5 rounded-2xl bg-white/10 text-white font-semibold flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download poster
          </button>
          <button data-testid="qr-tent" onClick={() => window.open(tentSrc, "_blank")}
            className="tap w-full h-13 py-3.5 rounded-2xl bg-white/10 text-white font-semibold flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download table tent (reception)
          </button>
          <button data-testid="qr-banner" onClick={() => window.open(bannerSrc, "_blank")}
            className="tap w-full h-13 py-3.5 rounded-2xl bg-white/10 text-white font-semibold flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Download share banner
          </button>

          {stats && (
            <div data-testid="scan-analytics" className="mt-2 rounded-2xl bg-white/10 p-4 text-white">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-[#8FE3BE]" />
                <p className="font-head font-bold text-sm">Scan reach</p>
              </div>
              <div className="flex items-end gap-6 mb-3">
                <div>
                  <p data-testid="scan-total" className="font-head font-extrabold text-2xl leading-none">{stats.total || 0}</p>
                  <p className="text-[11px] text-white/60 mt-1">Total scans</p>
                </div>
                <div>
                  <p className="font-head font-extrabold text-2xl leading-none">{stats.last_30_days || 0}</p>
                  <p className="text-[11px] text-white/60 mt-1">Last 30 days</p>
                </div>
              </div>
              {Object.keys(stats.by_source || {}).length > 0 ? (
                <ul className="flex flex-wrap gap-2">
                  {Object.entries(stats.by_source).map(([k, v]) => (
                    <li key={k} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#8FE3BE]/20 text-[#8FE3BE]">
                      {SRC_LABEL[k] || k} · {v}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11.5px] text-white/60">No scans yet — put these codes on your poster and table tent to start tracking reach.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
